import isEqual from 'lodash/isEqual';
import { axios } from '/plugins/axios.client';
import { i18n } from '/plugins/i18n.client';
import { errorFromResponse, encodeFieldsToBase64, replaceAndTranslateDynamicValues } from '/src/common/general-utils';
const errorHandler = errorFromResponse(i18n.global.t.bind(i18n));
import { toast } from '/plugins/toaster.client.js';

import { getRecordPayload } from './object-record-helper';

/**
 * Fetch Object record
 */
export const getObjectRecord = async (objectUUID, recordUUID, options = {}) => {
  try {
    return await axios.$get(`/api/object/${objectUUID}/records/${recordUUID}`, options);
  } catch (error) {
    toast.error(errorHandler('common.object_record.fetch_object_record', error));
    return undefined;
  }
};

/**
 * Fetch Object records
 */
export const getObjectRecords = async (objectUUID, options = {}) => {
  try {
    return await axios.$get(`/api/object/${objectUUID}/records`, options);
  } catch (error) {
    if (error?.message === 'canceled') {
      return { cancelled: true };
    }
    toast.error(errorHandler('common.object_record.fetch_object_record', error));
    return { data: [], recordsTotal: 0 };
  }
};

/**
 * Fetch Object records
 */
export const getTabularObjectRecords = async (objectUUID, fieldUUID, payload) => {
  try {
    return await axios.$post(`/api/object/tabular-reference/${objectUUID}/${fieldUUID}/records`, payload);
  } catch (error) {
    toast.error(errorHandler('common.object_record.fetch_object_record', error));
    return { data: [], recordsTotal: 0 };
  }
};

/**
 * Fetch Object records
 */
export const listFilterValues = async (objectUUID, fieldUUID, payload) => {
  try {
    const result = await axios.$post(
      `/api/object/tabular-reference/${objectUUID}/${fieldUUID}/listDynamicValuesV2`,
      payload
    );
    const fetchMoreValues = result.data.length >= payload.noOfRecords;
    return { filterResult: result.data, fetchMoreValues };
  } catch (error) {
    toast.error(
      errorHandler(
        'common.object_record.common.object_templates.ref_field_tabular_form.error_fetch_filter_values',
        error
      )
    );
    return { filterResult: [], fetchMoreValues: true };
  }
};

const isTimeoutError = (error) => error?.response?.status === 504 || error?.response?.data?.code === 504;

const subformChanged = (subformMap) => {
  let recordsDeleted = [];
  let recordsEdited = [];
  if (subformMap.delete) {
    recordsDeleted = Object.keys(subformMap.delete).filter((objectUUID) => subformMap.delete[objectUUID].length > 0);
  }
  if (subformMap.edit) {
    recordsEdited = Object.keys(subformMap.edit).filter((objectUUID) => subformMap.edit[objectUUID].length > 0);
  }
  return recordsDeleted.length === 0 && recordsEdited.length === 0;
};

const subformUpdates = (subformMap) => {
  let subformUpdates = { delete: {}, edit: {}, add: {} };

  if (subformMap.delete) {
    subformUpdates.delete = Object.keys(subformMap.delete).reduce((result, objectUUID) => {
      if (subformMap.delete[objectUUID].length) {
        result[objectUUID] = subformMap.delete[objectUUID];
      }
      return result;
    }, {});
  }

  if (subformMap.edit) {
    subformUpdates = Object.keys(subformMap.edit).reduce((result, objectUUID) => {
      // filtering already deleted records
      const recordDetails = subformMap.edit[objectUUID].filter(
        (record) => !subformUpdates.delete[objectUUID] || subformUpdates.delete[objectUUID].indexOf(record.UUID) === -1
      );
      if (recordDetails.length) {
        subformUpdates.edit[objectUUID] = recordDetails
          .filter((record) => record.newRecord !== true)
          .map((recordToAdd) => recordToAdd.UUID);

        subformUpdates.add[objectUUID] = recordDetails
          .filter((record) => record.newRecord === true)
          .map((recordToAdd) => recordToAdd.UUID);
      }
      return result;
    }, subformUpdates);
  }
  return subformUpdates;
};

const trimObjectFieldsData = (modifiedRecord, fields) => {
  const localRecord = { ...modifiedRecord };
  fields.forEach((field) => {
    switch (field.FieldType) {
      case 'SHORT_TEXT':
      case 'LONG_TEXT':
        localRecord[field.DatabaseColumnName] =
          typeof localRecord[field.DatabaseColumnName] === 'string'
            ? localRecord[field.DatabaseColumnName].trim()
            : localRecord[field.DatabaseColumnName];
        break;
      case 'TAG':
        localRecord[field.DatabaseColumnName] = localRecord[field.DatabaseColumnName]?.map((e) => e.trim());
        break;
    }
  });
  return localRecord;
};

/**
 * Saves object record
 */
export const saveObjectRecord = async (
  { objectUUID, fields },
  { modifiedRecord, record, filesAdded, fileFields, subformMap = {} },
  options = {},
  { isWithoutSave, isEditableExternal, isExternal, externalFiles, isSubmitted, captchaResponse } = {}
) => {
  try {
    modifiedRecord = trimObjectFieldsData(modifiedRecord, fields);

    //get the payload for record saving
    let payload = getRecordPayload(fields, modifiedRecord);

    //Get the record to compare with payload after removing fields which are not needed for comparison
    const { ID, CreatedAt, CreatedBy, UpdatedAt, UpdatedBy, fileDetails, ...recordToCompare } = record;

    // show no modifications toast if payload is same as that of retrieved record
    if (
      (!isExternal || (isEditableExternal && !isSubmitted)) &&
      !fileFields &&
      payload.UUID &&
      isEqual(payload, recordToCompare) &&
      subformChanged(subformMap)
    ) {
      return { saved: false, noModification: true, recordUUID: payload.UUID };
    }

    if (isExternal) {
      if (isEditableExternal) {
        fileFields && (payload.FilesAdded = JSON.stringify(externalFiles));
        let result = await axios.$put(
          `/object/${objectUUID}/external/edit`,
          { ...payload, ...(isSubmitted && { captchaResponse, IsSubmit: true }) },
          options
        );
        return { saved: true, recordUUID: result.UUID, record: { ...payload } };
      }
      if (isSubmitted) {
        delete payload.UUID;
        fileFields && (payload.FilesAdded = JSON.stringify(externalFiles));
        let result = await axios.$post(`/object/${objectUUID}/create`, { ...payload, captchaResponse }, options);
        return { saved: true, recordUUID: result.UUID, record: { ...payload } };
      }
      payload.UUID = 'external_UUID';
      return { saved: true, record: { ...payload } };
    }

    // When data needed without save.
    if (isWithoutSave) {
      return { objectUUID: objectUUID, payload, options };
    }

    payload.FilesAdded = JSON.stringify(filesAdded);
    payload.subformMap = JSON.stringify(subformUpdates(subformMap));

    let result = await axios.$post(`/api/object/${objectUUID}/records`, payload, options);
    return {
      saved: true,
      recordUUID: result.UUID,
      detailedAuditId: result.detailedAuditId,
      payload: { ...payload, subformMap: options.params?.isSubform ? subformMap : payload.subformMap }
    };
  } catch (error) {
    if (error.response?.data?.errorData) {
      const fileNames = Object.values(error.response.data.errorData).reduce((acc, value) => {
        acc = [...acc, ...value];
        return acc;
      }, []);
      toast.warn(fileNames, error.response.data.message);
      return { saved: false, errorData: error.response.data.errorData };
    }
    toast.error(
      errorHandler(
        i18n.global.t('common.dynamic_labels.saving_action', {
          feature: i18n.global.t('common.feature.object_record')
        }),
        error
      )
    );
    return { saved: false };
  }
};

/**
 * Upload attachment
 * @param {String} objectUUID
 * @param {Object} file
 */
export const uploadTemporaryAttachment = async (
  { objectUUID, file, recordUUID, fieldUUID },
  { headers, tenantEntryUUID },
  setUploadProgress,
  removeUploadProgress
) => {
  try {
    const data = new FormData();
    data.append(file.name, file);
    let percent;
    const response = await axios.$post(`/api/object/temporarilyUploadAttachments/${objectUUID}`, data, {
      params: { recordUUID: tenantEntryUUID ? null : recordUUID, tenantEntryUUID, fieldUUID },
      headers,
      onUploadProgress: (e) => {
        percent = Math.round((e.loaded * 100) / e.total);
        setUploadProgress({ id: file.id, value: percent });
      }
    });
    removeUploadProgress(file.id);
    toast.success(i18n.global.t('common.common_messages.file_upload_success'));
    return { uniqueName: response.uniqueName, isError: false };
  } catch (error) {
    removeUploadProgress(file.id);
    toast.error(error?.response?.data?.error || i18n.global.t('common.common_components.file_upload.upload_error'));
    return { isError: true };
  }
};

/**
 * Deletes attachment
 * @param {String} objectUUID
 * @param {String} recordUUID
 * @param {String} fieldUUID
 * @param {String} filename
 */
export const removeAttachment = async ({ objectUUID, recordUUID, fieldUUID, filename }, params = {}) => {
  try {
    await axios.$delete(`/api/object/attachment/${objectUUID}/${recordUUID}/${fieldUUID}/field/${filename}`, {
      params
    });
    toast.success(i18n.global.t('common.common_messages.file_removed'));
    return true;
  } catch (error) {
    toast.error(i18n.global.t('common.common_messages.file_remove_error'));
    return false;
  }
};

/**
 * Fetch object record
 * @param {String} objectUUID
 * @param {String} recordUUID
 */
export const getRecordFromView = async (objectUUID, recordUUID, options = {}) => {
  try {
    return await axios.$get(`/api/object/${objectUUID}/records-view/${recordUUID}`, options);
  } catch (error) {
    toast.error(errorHandler('common.object_record.fetch_object_record', error));
    return undefined;
  }
};

export const getRecordChangeLogs = async (objectUUID, recordUUID) => {
  try {
    const changeLog = await axios.$get(`/api/object/${objectUUID}/audit-trail/${recordUUID}`);
    const list = changeLog.map((log) =>
      Object.assign(log, {
        Data: JSON.parse(log.Data),
        OldValue: log.OldValue ? JSON.parse(log.OldValue) : {}
      })
    );
    return { list, loading: false };
  } catch (error) {
    toast.error(i18n.global.t('common.object_overview_page.fetch_change_log_error'));
    return { list: [], loading: false };
  }
};

/**
 * Gets data of data loader
 * @param {String} businessRuleUUID
 * @param {String} condition
 * @param {Object} record
 */
export const getDataForDataLoader = async (businessRuleDetails, record, externalTempDetails = {}) => {
  const { businessRuleUUID, condition, name } = businessRuleDetails;
  const fieldsToEncode = ['filter'];
  const payload = encodeFieldsToBase64(
    {
      filter: Object.keys(record).length > 0 ? replaceAndTranslateDynamicValues(condition, record) : condition
    },
    fieldsToEncode
  );
  try {
    return externalTempDetails.tenantEntryUUID
      ? await axios.$post(
          `/api/v2/business-rules/external/getDataForDataLoader/${businessRuleUUID}?tenantEntryUUID=${externalTempDetails.tenantEntryUUID}`,
          payload,
          {
            headers: {
              'template-uuid': externalTempDetails.templateUUID
            }
          }
        )
      : await axios.$post(`/api/v2/business-rules/getDataForDataLoader/${businessRuleUUID}`, payload);
  } catch (error) {
    toast.error(errorHandler(i18n.global.t('common.object_record.fetch_dataloader_data', { name }), error));
    return {};
  }
};

/**
 * executes trigger based on passed parameters
 *
 * @param {Object} objectDetails contains fields, object and trigger
 * @param {Object} record contains record
 */
export const executeTrigger = async (triggerUUID, object, fields, { record, dataloaderFields = {}, templateUUID }) => {
  try {
    //get the payload for record to be saved
    const recordPayload = getRecordPayload(fields, record, true);

    // replacing undefined with null as template triggers does not work if column not available.
    // and axios removes undefined keys from body
    const finalPayload = Object.keys(recordPayload).reduce((detail, key) => {
      detail[key] = recordPayload[key] === undefined ? null : recordPayload[key];
      return detail;
    }, {});

    return await axios.$put(
      `/api/triggers/Object Template/execute/${object.UUID}/${triggerUUID}`,
      { ...finalPayload, ...dataloaderFields },
      {
        params: {
          recordUUID: finalPayload.UUID,
          objDatabaseView: object.DatabaseView,
          templateUUID
        }
      }
    );
  } catch (error) {
    toast.error(
      i18n.global.t(
        isTimeoutError(error)
          ? 'common.object_templates.timeout_error_message'
          : 'common.object_templates.error_message'
      )
    );
    throw error;
  }
};
